import React, { useState } from 'react';
import { MMMConfigFormState } from '../../types/mmmConfig';

interface CalendarAndSeasonalitySectionProps {
  config: MMMConfigFormState;
  onChange: (config: Partial<MMMConfigFormState>) => void;
  errors?: Record<string, string | undefined>;
}

export const CalendarAndSeasonalitySection: React.FC<CalendarAndSeasonalitySectionProps> = ({
  config,
  onChange,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleTrendToggle = (value: boolean) => {
    onChange({
      calendar: {
        ...config.calendar,
        use_trend: value,
      },
    });
  };

  const handleSeasonalityToggle = (value: boolean) => {
    onChange({
      calendar: {
        ...config.calendar,
        use_seasonality: value,
      },
    });
  };

  const handleSeasonalityPeriodsChange = (period: 'weekly' | 'monthly' | 'yearly', enabled: boolean) => {
    const currentPeriods = config.calendar?.seasonality_periods || [52];
    let newPeriods: number[];

    if (period === 'weekly') {
      if (enabled && !currentPeriods.includes(7)) {
        newPeriods = [...currentPeriods, 7].sort((a, b) => a - b);
      } else {
        newPeriods = currentPeriods.filter((p) => p !== 7);
      }
    } else if (period === 'monthly') {
      if (enabled && !currentPeriods.includes(30)) {
        newPeriods = [...currentPeriods, 30].sort((a, b) => a - b);
      } else {
        newPeriods = currentPeriods.filter((p) => p !== 30);
      }
    } else {
      if (enabled && !currentPeriods.includes(52)) {
        newPeriods = [...currentPeriods, 52].sort((a, b) => a - b);
      } else {
        newPeriods = currentPeriods.filter((p) => p !== 52);
      }
    }

    if (newPeriods.length === 0) {
      newPeriods = [52];
    }

    onChange({
      calendar: {
        ...config.calendar,
        seasonality_periods: newPeriods,
      },
    });
  };

  const handleProphetToggle = (value: boolean) => {
    onChange({
      decomposition: {
        ...config.decomposition,
        use_prophet: value,
      },
    });
  };

  const handleYearlySeasonalityToggle = (value: boolean) => {
    onChange({
      decomposition: {
        ...config.decomposition,
        yearly_seasonality: value,
      },
    });
  };

  const handleWeeklySeasonalityToggle = (value: boolean) => {
    onChange({
      decomposition: {
        ...config.decomposition,
        weekly_seasonality: value,
      },
    });
  };

  const handleDailySeasonalityToggle = (value: boolean) => {
    onChange({
      decomposition: {
        ...config.decomposition,
        daily_seasonality: value,
      },
    });
  };

  const handleCountryHolidaysChange = (value: string) => {
    onChange({
      decomposition: {
        ...config.decomposition,
        country_holidays: value || null,
      },
    });
  };

  const seasonalityPeriods = config.calendar?.seasonality_periods || [52];

  const checkboxClasses = "w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-2 focus:ring-gray-100 focus:ring-offset-0";

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-5">Calendar & Seasonality</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.calendar?.use_trend !== false}
              onChange={(e) => handleTrendToggle(e.target.checked)}
              className={checkboxClasses}
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Include Trend</span>
          </label>
          <p className="text-xs text-gray-500 mt-2 ml-6">
            Captures long-term direction in your data
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.calendar?.use_seasonality !== false}
              onChange={(e) => handleSeasonalityToggle(e.target.checked)}
              className={checkboxClasses}
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Include Seasonality</span>
          </label>
          <p className="text-xs text-gray-500 mt-2 ml-6">
            Captures recurring weekly, monthly, yearly patterns
          </p>
        </div>
      </div>

      {config.calendar?.use_seasonality !== false && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-5">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Seasonality Periods</h3>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={seasonalityPeriods.includes(7)}
                onChange={(e) => handleSeasonalityPeriodsChange('weekly', e.target.checked)}
                className={checkboxClasses}
              />
              <span className="ml-2 text-sm text-gray-600">
                Weekly (7 days) - Day-of-week effects
              </span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={seasonalityPeriods.includes(30)}
                onChange={(e) => handleSeasonalityPeriodsChange('monthly', e.target.checked)}
                className={checkboxClasses}
              />
              <span className="ml-2 text-sm text-gray-600">
                Monthly (30 days) - Month patterns
              </span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={seasonalityPeriods.includes(52)}
                onChange={(e) => handleSeasonalityPeriodsChange('yearly', e.target.checked)}
                className={checkboxClasses}
              />
              <span className="ml-2 text-sm text-gray-600">
                Yearly (52 weeks) - Annual cycles
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Recommended: Weekly + yearly for marketing data
          </p>
        </div>
      )}

      <div className="border-t border-gray-200 pt-5">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-gray-600 hover:text-gray-900 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          <span className="text-xs">{showAdvanced ? '-' : '+'}</span>
          Advanced: Prophet Decomposition
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.decomposition?.use_prophet === true}
                onChange={(e) => handleProphetToggle(e.target.checked)}
                className={checkboxClasses}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Use Prophet Decomposition
              </span>
            </label>
            <p className="text-xs text-gray-500 ml-6">
              Decompose target into trend, seasonal, and residual components
            </p>
            <div className="ml-6 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <strong>Warning:</strong> Prophet can absorb media effects as "seasonality" if spend patterns correlate with time.
              This may reduce model accuracy. Start with Prophet OFF for better results.
            </div>

            {config.decomposition?.use_prophet === true && (
              <div className="ml-6 pt-3 border-t border-gray-200 space-y-3">
                <p className="text-sm font-medium text-gray-700">Seasonality Components:</p>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.decomposition?.yearly_seasonality !== false}
                    onChange={(e) => handleYearlySeasonalityToggle(e.target.checked)}
                    className={checkboxClasses}
                  />
                  <span className="ml-2 text-sm text-gray-600">Yearly</span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.decomposition?.weekly_seasonality !== false}
                    onChange={(e) => handleWeeklySeasonalityToggle(e.target.checked)}
                    className={checkboxClasses}
                  />
                  <span className="ml-2 text-sm text-gray-600">Weekly</span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.decomposition?.daily_seasonality !== false}
                    onChange={(e) => handleDailySeasonalityToggle(e.target.checked)}
                    className={checkboxClasses}
                  />
                  <span className="ml-2 text-sm text-gray-600">Daily</span>
                </label>

                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Country Holidays
                  </label>
                  <select
                    value={config.decomposition?.country_holidays || ''}
                    onChange={(e) => handleCountryHolidaysChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-900"
                  >
                    <option value="">None</option>
                    <option value="US">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="JP">Japan</option>
                    <option value="IN">India</option>
                    <option value="BR">Brazil</option>
                    <option value="MX">Mexico</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="info-box">
        <h3 className="info-title">Configuration Guide</h3>
        <ul>
          <li><strong>Trend:</strong> Captures long-term direction (enable for most cases)</li>
          <li><strong>Seasonality:</strong> Weekly (day-of-week), Monthly (patterns), Yearly (holidays)</li>
          <li><strong>Prophet:</strong> Advanced time-series decomposition</li>
          <li><strong>Recommended:</strong> Enable trend + weekly + yearly seasonality</li>
        </ul>
      </div>
    </div>
  );
};
